/**
 * Live Link Status Service
 * Frontend service for live link monitoring
 */

import { apiService } from './api';

export interface LiveLinkStatus {
  id: string;
  liveUrl: string;
  status: 'PENDING' | 'CHECKING' | 'OK_200' | 'ERROR_4XX' | 'ERROR_5XX' | 'TIMEOUT' | 'UNKNOWN';
  statusCode: number | null;
  statusMessage: string | null;
  lastCheckedAt: string | null;
  nextCheckAt: string | null;
  isExternal: boolean;
  publisherId: string | null;
  placementId: string | null;
  createdAt: string;
  updatedAt: string;
  publisher?: {
    id: string;
    publisherName: string;
    email: string;
  };
  placement?: {
    id: string;
    placementId: string;
    guestBlogSite: {
      site_url: string;
    };
  };
}

export interface ImportUrlItem {
  url: string;
  publisherEmail?: string;
}

export interface LiveLinkStatusStats {
  total: number;
  ok: number;
  error4xx: number;
  error5xx: number;
  timeout: number;
  pending: number;
  errorTotal: number;
}

class LiveLinkStatusService {
  private baseUrl = '/live-link-status';

  /**
   * Get list of live link statuses with filtering and pagination
   */
  async getLiveLinkStatuses(params: {
    status?: string;
    type?: string;
    publisherId?: string;
    clientId?: string;
    projectId?: string;
    month?: number;
    year?: number;
    search?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<{
    success: boolean;
    data: {
      linkStatuses: LiveLinkStatus[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        totalPages: number;
      };
    };
  }> {
    const queryParams = new URLSearchParams();
    if (params.status) queryParams.append('status', params.status);
    if (params.type) queryParams.append('type', params.type);
    if (params.publisherId) queryParams.append('publisherId', params.publisherId);
    if (params.clientId) queryParams.append('clientId', params.clientId);
    if (params.projectId) queryParams.append('projectId', params.projectId);
    if (params.month) queryParams.append('month', params.month.toString());
    if (params.year) queryParams.append('year', params.year.toString());
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page.toString());
    if (params.limit) queryParams.append('limit', params.limit.toString());

    const url = `${this.baseUrl}?${queryParams.toString()}`;
    return apiService.get(url);
  }

  /**
   * Get statistics for live link statuses
   */
  async getStats(): Promise<{
    success: boolean;
    data: LiveLinkStatusStats;
  }> {
    return apiService.get(`${this.baseUrl}/stats`);
  }

  /**
   * Import URLs from CSV
   */
  async importUrls(urls: ImportUrlItem[]): Promise<{
    success: boolean;
    message: string;
    data: {
      success: string[];
      failed: Array<{ url: string; error: string }>;
    };
  }> {
    return apiService.post(`${this.baseUrl}/import`, { urls });
  }

  /**
   * Manually check a single link
   */
  async checkLink(id: string): Promise<{
    success: boolean;
    message: string;
    data: any;
  }> {
    return apiService.post(`${this.baseUrl}/${id}/check`);
  }

  /**
   * Update link status manually
   */
  async updateLinkStatus(
    id: string,
    data: {
      status: string;
      statusMessage?: string;
      nextCheckAt?: string;
    }
  ): Promise<{
    success: boolean;
    message: string;
    data: LiveLinkStatus;
  }> {
    return apiService.put(`${this.baseUrl}/${id}`, data);
  }

  /**
   * Delete a link status
   */
  async deleteLinkStatus(id: string): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiService.delete(`${this.baseUrl}/${id}`);
  }

  /**
   * Trigger check of all due links (returns job ID for tracking)
   */
  async checkAllLinks(): Promise<{
    success: boolean;
    message: string;
    jobId: string | null;
    totalLinks: number;
  }> {
    return apiService.post(`${this.baseUrl}/check-all`);
  }

  /**
   * Check status of a Check All background job
   */
  async getJobStatus(jobId: string): Promise<{
    success: boolean;
    data: {
      id: string;
      status: 'running' | 'completed' | 'failed';
      progress: { checked: number; total: number };
      message: string;
      startedAt: string;
      completedAt?: string;
      error?: string;
    };
  }> {
    return apiService.get(`${this.baseUrl}/check-status/${jobId}`);
  }

  /**
   * Trigger auto-population from placements
   */
  async populateFromPlacements(): Promise<{
    success: boolean;
    message: string;
  }> {
    return apiService.post(`${this.baseUrl}/populate`);
  }

  /**
   * Get status color for display
   */
  getStatusColor(status: LiveLinkStatus['status']): 'success' | 'error' | 'warning' | 'info' | 'default' {
    switch (status) {
      case 'OK_200':
        return 'success';
      case 'ERROR_4XX':
      case 'ERROR_5XX':
        return 'error';
      case 'TIMEOUT':
        return 'warning';
      case 'CHECKING':
        return 'info';
      case 'PENDING':
      case 'UNKNOWN':
      default:
        return 'default';
    }
  }

  /**
   * Get display label for status
   */
  getStatusLabel(status: LiveLinkStatus['status']): string {
    switch (status) {
      case 'OK_200':
        return 'OK (200)';
      case 'ERROR_4XX':
        return '4xx Error';
      case 'ERROR_5XX':
        return '5xx Error';
      case 'TIMEOUT':
        return 'Timeout';
      case 'CHECKING':
        return 'Checking';
      case 'PENDING':
        return 'Pending';
      case 'UNKNOWN':
        return 'Unknown / Other';
      default:
        return status;
    }
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string | null): string {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Get time ago string
   */
  getTimeAgo(dateString: string | null): string {
    if (!dateString) return '-';
    
    const date = new Date(dateString);
    const now = new Date();
    
    // Calculate time difference in milliseconds
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    // Compare actual calendar dates for Today/Yesterday
    const todayStr = now.toISOString().split('T')[0]; // YYYY-MM-DD format
    const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    
    if (dateStr === todayStr) {
      // Same day - show granular time
      if (diffMinutes < 1) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
      return `${diffHours} hours ago`;
    }
    
    // Check if it's yesterday
    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];
    
    if (dateStr === yesterdayStr) {
      return 'Yesterday';
    }
    
    // For older dates, use day-based calculations
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  }
}

export const liveLinkStatusService = new LiveLinkStatusService();
